import { Injectable } from '@angular/core';
import * as moment from 'moment';

import { SelectFieldOption } from '../../components/select-field-getm/select-field-getm.component';
import { IDecodeJwt } from '../../models/Tenderos.interface';

@Injectable({
  providedIn: 'root'
})
export class UtilitiesService {

  constructor() { }

  public formatSelect = (reasonContact: Array<any>, idType: string, name: string) =>
    reasonContact.length > 0 ? reasonContact.map(x => ({ id: x[idType], name: x[name] })) : []

  /**
   * Realiza el decode del JWT
   */
  public getDecodeTokenJwt = (jwt: string): IDecodeJwt => {

    if (this.isNullOrEmpty(jwt)) { return null; }

    const base64Url = String(jwt).split('.')[1];
    if (!base64Url) { return null; }

    const base64 = base64Url.replace('-', '+').replace('_', '/');

    return JSON.parse(this.b64DecodeUnicode(base64));
  }

  /**
   * Realiza el decode del jwt del base con UTF8
   */
  private b64DecodeUnicode = (base64: string) => {
    return decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  }

  /**
   * Método que valida un string no este vacío, nulo o undefined
   * @param value es el string que se va a validar si cumple el requisito
   * @returns true o false si cumple con la regla
   */
  public isNullOrEmpty = (value: string) => !(typeof value === 'string' && value.length > 0);

  /**
   * Convierte el primer caracter de cada palabra en mayuscula
   * @param value es el string a convertir
   */
  public titleCase = (value: string) => value.toLowerCase().replace(/\b(\s\w|^\w)/g, s => s.toUpperCase());

  /**
   * Convierte el primer caracter de una cadena en mayuscula
   * @param value es el string a convertir
   */
  public capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

  /**
   * Formatea a dos decimales el valor
   * @param number valor a reducir a dos decimales
   */
  public twoDecimal = (numero: number) => Math.round((numero + Number.EPSILON) * 100) / 100;

  public currency = (value: number) => new Intl.NumberFormat
    ('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value)

  public normalizeCategoryName = (name: string) => name.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(' ', '-');

  public ramdonNumber = (minimo: number, maximo: number) => Math.round(Math.random() * (maximo - minimo) + minimo);

  /**
   * Obtiene la cantidad de cifras/dígitos de un número.
   * Si no es un número válido, retorna 0.
   * 
   * @param value El número o cadena de texto que se va a evaluar
   * @returns El número de dígitos (cifras)
   */
  public getDigitsCount = (value: number | string | undefined | null): number => {
    if (value === undefined || value === null) {
      return 0;
    }
    const cleaned = value.toString().replace(/\D/g, '');
    return cleaned.length;
  }

  public equalObjectsArray = (o1: Array<object>, o2: Array<object>) =>
    Object.keys(o1).length === Object.keys(o2).length
    && Object.keys(o1).every(p => o1[p] === o2[p])

  /**
   * Valida que la fecha de expiracion del token siga siendo vigente
   * comparandolo con la fecha actual del dispositivo
   * @param jwt Token de acceso con fecha de expiracion
   */
  public isActiveToken = (jwt: string): boolean => {
    if (jwt && jwt.length > 0) {
      const dataToken = this.getDecodeTokenJwt(jwt);
      return dataToken.exp >= new Date().getTime();
    } else {
      return false;
    }
  }


  /**
   * Normalizes text by converting to lowercase, removing spaces, and eliminating diacritical marks (accents).
   * 
   * @param text - The string to normalize
   * @returns The normalized string with no spaces, no accents, and all lowercase
   * 
   * @example
   * // Returns "helloworld"
   * normalizeText("Hello World");
   * 
   * @example
   * // Returns "accion"
   * normalizeText("Acción");
   */
  public normalizeText = (text: string, removeSpaces: boolean = true): string => {
    let normalized = text.toString()
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    if (removeSpaces) {
      normalized = normalized.replace(/ /g, '');
    }

    return normalized;
  }

  /**
   * Obtiene el rango de fechas para el año actual
   * Caso 1: 12 meses atrás (ej. Feb 2025 a Ene 2026)
   * Caso 2: Solo año actual (desde Enero hasta el mes presente)
   * 
   * @param show12MonthsAgo Indica si se debe mostrar el rango de fechas para los últimos 12 meses
   * @param formatDateLabel Formato de fecha para el label de fechas a mostrar
   * 
   * @returns Un array de SelectFieldOption con el rango de fechas
   */
  public getDatesRangeForYear = (show12MonthsAgo: boolean, formatDateLabel: Intl.DateTimeFormatOptions): SelectFieldOption[] => {
    const formatter = new Intl.DateTimeFormat('es-ES', formatDateLabel);
    const today = new Date();

    let fechaInicio: Date;
    let fechaFin: Date = new Date(today.getFullYear(), today.getMonth(), 1);

    if (show12MonthsAgo) {
      // * Caso 1: 12 meses atrás (ej. Feb 2025 a Ene 2026)
      fechaInicio = new Date(today.getFullYear(), today.getMonth() - 11, 1);
    } else {
      // * Caso 2: Solo año actual (desde Enero hasta el mes presente)
      fechaInicio = new Date(today.getFullYear(), 0, 1);
    }

    const periodos: SelectFieldOption[] = [];
    let fechaCursor = new Date(fechaInicio);

    while (fechaCursor <= fechaFin) {
      const monthFormatted = formatter.format(fechaCursor);
      const monthLabel = this.capitalize(monthFormatted);

      periodos.push({
        value: fechaCursor.toISOString().split('T')[0],
        label: monthLabel
      });

      fechaCursor.setMonth(fechaCursor.getMonth() + 1);
    }

    return periodos.reverse();
  }

  /**
   * Helper DRY: Calcula la diferencia absoluta en días entre una fecha objetivo y la fecha actual.
   * Retorna null si la fecha está vacía o es inválida.
   *
   * @param fechaObjetivo Cadena de texto con la fecha a evaluar (formato ISO o YYYY-MM-DD)
   * @returns Diferencia en días como número absoluto, o null si la fecha no es válida
   */
  public obtenerDiferenciaEnDias(fechaObjetivo: string): number | null {
    if (!fechaObjetivo) return null;

    const today = moment().startOf('day');
    const targetDay = moment(fechaObjetivo).startOf('day');
    if (!targetDay.isValid()) return null;
    return Math.abs(targetDay.diff(today, 'days'));
  }

  /**
   * Helper: Formatea una fecha en formato estándar DD/MM/YYYY (ej. 30/09/2026).
   * Retorna cadena vacía ante fechas nulas o inválidas.
   *
   * @param fecha Cadena de texto con la fecha a formatear
   * @returns Fecha en formato 'DD/MM/YYYY' o '' si es inválida
   */
  public formatearFechaTermino(fecha: string): string {
    if (!fecha) return '';
    const fechaMoment = moment(fecha);
    return fechaMoment.isValid() ? fechaMoment.format('DD/MM/YYYY') : '';
  }

}
